import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { RBCONotice } from '@/components/withdrawals/RBCONotice';
import { RMDFlow } from '@/components/withdrawals/RMDFlow';
import { BankAccountsPanel } from '@/components/withdrawals/BankAccountsPanel';
import { ActiveLoansPanel } from '@/components/withdrawals/ActiveLoansPanel';
import { InServiceWithdrawalPanel } from '@/components/withdrawals/InServiceWithdrawalPanel';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export const LoansPage = () => {
  const [maxLoan, setMaxLoan] = useState(null);
  const [rate, setRate] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [formData, setFormData] = useState({ loan_type: 'general_purpose', amount: '', term: 60, purpose: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accountId, setAccountId] = useState('');
  const formRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  // Auto-scroll + focus first input when the form opens (Bug #3 fix — was:
  // button at top of page but form rendered below 6+ sections, making it
  // look like the button "did nothing").
  useEffect(() => {
    if (showApply && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => firstInputRef.current?.focus(), 300);
    }
  }, [showApply]);

  const loadData = async () => {
    try {
      const acctRes = await api.get('/accounts/me');
      const aid = acctRes.data?.id;
      setAccountId(aid || '');
      if (aid) {
        try { const r = await api.get(`/loans/rules/maximum-loan-amount?vested_balance=100000`); setMaxLoan(r.data); } catch {}
        try { const r = await api.get('/loans/rules/interest-rate'); setRate(r.data); } catch {}
      }
    } catch {}
  };

  const handleApply = async () => {
    if (!formData.purpose || formData.purpose.length < 10) { setError('Purpose must be at least 10 characters'); return; }
    const amt = parseFloat(formData.amount);
    if (!amt || amt < 1000 || amt > 50000) { setError('Amount must be $1,000 - $50,000'); return; }
    setSubmitting(true); setError('');
    try {
      await api.post('/loans/applications', {
        account_id: accountId, loan_type: formData.loan_type,
        requested_amount: amt, requested_term_months: formData.term,
        purpose: formData.purpose, payment_frequency: 'monthly'
      });
      setShowApply(false);
      setFormData({ loan_type: 'general_purpose', amount: '', term: 60, purpose: '' });
      loadData();
    } catch (e) { setError(e.response?.data?.detail || 'Application failed'); }
    finally { setSubmitting(false); }
  };

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  return (
    <div className="space-y-4" data-testid="loans-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Withdrawals &amp; Loans</h1>
        <button
          onClick={() => setShowApply(!showApply)}
          data-testid="apply-loan-btn"
          aria-expanded={showApply}
          className="flex items-center gap-2 px-4 py-2 bg-[#1B2A4A] text-white text-sm rounded hover:bg-[#2D3F5F] transition-colors"
        >
          {showApply ? <X size={14} /> : <Plus size={14} />}
          {showApply ? 'Close Application' : 'Apply for Loan'}
        </button>
      </div>

      {/* Loan Application Form — rendered immediately below the header so
          clicking "Apply for Loan" produces instant, visible feedback. */}
      {showApply && (
        <div ref={formRef} className="panel p-5 border-l-4 border-l-[#1B2A4A]" data-testid="loan-application-form">
          <div className="flex items-center justify-between mb-3">
            <span className="overline">New Loan Application</span>
            <span className="text-[10px] text-[#9CA3AF] font-mono">5 C.F.R. Part 1655</span>
          </div>
          {error && (
            <div className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="loan-application-error">
              <AlertTriangle size={14} className="text-red-600" />
              <span className="text-xs text-red-700">{error}</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Loan Type</label>
              <select
                ref={firstInputRef}
                value={formData.loan_type}
                onChange={e => setFormData({ ...formData, loan_type: e.target.value, term: e.target.value === 'residential' ? 180 : 60 })}
                className={inputClass}
                data-testid="loan-type-select"
              >
                <option value="general_purpose">General Purpose (1-5 years)</option>
                <option value="residential">Residential (1-15 years)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Amount ($1,000 - $50,000)</label>
              <input type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} placeholder="10000" className={inputClass} data-testid="loan-amount-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Term (months)</label>
              <input type="number" value={formData.term} onChange={e => setFormData({ ...formData, term: parseInt(e.target.value) || 60 })} min={12} max={formData.loan_type === 'residential' ? 180 : 60} className={inputClass} data-testid="loan-term-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Purpose (min 10 chars)</label>
              <input type="text" value={formData.purpose} onChange={e => setFormData({ ...formData, purpose: e.target.value })} placeholder="Brief description of loan purpose" className={inputClass} data-testid="loan-purpose-input" />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={handleApply} disabled={submitting} data-testid="submit-loan-btn" className="px-4 py-2 bg-[#1B2A4A] text-white text-sm rounded hover:bg-[#2D3F5F] disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
            <button onClick={() => setShowApply(false)} className="px-4 py-2 border border-[#D1D5DB] text-sm rounded text-[#6B7280] hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Loan Limits summary (above other panels when data is available) */}
      {maxLoan && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="loan-limits-summary">
          <div className="panel p-4"><span className="overline">Max Loan Amount</span><div className="font-mono text-xl font-semibold text-[#1B2A4A] mt-1">{fmt(maxLoan.maximum_amount || 50000)}</div></div>
          <div className="panel p-4"><span className="overline">Vested Balance</span><div className="font-mono text-xl font-semibold text-[#1B2A4A] mt-1">{fmt(maxLoan.vested_balance)}</div></div>
          <div className="panel p-4"><span className="overline">Interest Rate</span><div className="font-mono text-xl font-semibold text-[#1B2A4A] mt-1">{rate?.interest_rate ? `${(rate.interest_rate * 100).toFixed(2)}%` : 'A Fund Rate'}</div></div>
        </div>
      )}

      {/* Rules Reference */}
      <div className="panel p-4 bg-blue-50 border-blue-200">
        <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">5 C.F.R. Part 1655 — UniFed Core Loan Rules</span>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-blue-700">
          <div>General Purpose: $1,000–$50,000, 1–5 year term</div>
          <div>Residential: $1,000–$50,000, 1–15 year term</div>
          <div>Max: lesser of $50,000 or 50% vested balance</div>
          <div>Separation: repay within 90 days or taxable distribution</div>
        </div>
      </div>

      {/* Permanent RBCO statutory notice — cannot be dismissed */}
      <RBCONotice />

      {/* RMD Flow — SECURE 2.0 Required Minimum Distributions */}
      <RMDFlow />

      {/* In-Service Withdrawal — Age-based (≥59½) and Hardship — 5 U.S.C. § 8433(h) */}
      <InServiceWithdrawalPanel />

      {/* Linked Bank Accounts for disbursements */}
      <BankAccountsPanel />

      {/* Active Loans with repayment progress + separation warning */}
      <ActiveLoansPanel />
    </div>
  );
};
